import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { clearSession } from "../api/auth";
import { meApi, meQueryKeys } from "../api/me";
import { setPlatformAdminFlag } from "../api/platform";
import { BranchSwitcher } from "./BranchSwitcher";
import { GlobalSearch } from "./GlobalSearch";
import { NotificationBell } from "./NotificationBell";
import { ThemeToggle } from "./ThemeToggle";

/** Humanise an RBAC role code into a Vietnamese label for the topbar chip. */
const ROLE_LABELS: Record<string, string> = {
  clinic_owner: "Chủ phòng khám",
  doctor: "Bác sĩ",
  nurse: "Điều dưỡng",
  receptionist: "Lễ tân",
  cashier: "Thu ngân",
  manager: "Quản lý",
  pharmacist: "Dược sĩ",
  admin: "Quản trị viên",
  platform_superadmin: "Quản trị hệ thống",
};

function roleLabel(codes: string[] | undefined): string {
  if (!codes || codes.length === 0) return "Nhân viên";
  return ROLE_LABELS[codes[0]] ?? "Nhân viên";
}

/** Live clock + Vietnamese date, matching the reference workspace chrome. */
function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const hhmm = now.toLocaleTimeString("vi-VN", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const date = now.toLocaleDateString("vi-VN", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return (
    <div className="hidden text-right leading-tight lg:block">
      <div className="text-sm font-semibold tabular-nums text-foreground">{hhmm}</div>
      <div className="text-xs text-muted-foreground">{date}</div>
    </div>
  );
}

export function TopBar({
  onMenuToggle,
  navOpen = false,
}: {
  onMenuToggle?: () => void;
  navOpen?: boolean;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: me } = useQuery({
    queryKey: meQueryKeys.profile(),
    queryFn: () => meApi.get(),
    staleTime: 5 * 60 * 1000,
  });

  // #logout — drop the token AND the cached queries; otherwise the previous user's
  // cached /me profile (staleTime 5m) is shown after the next login on the same tab.
  function handleLogout() {
    clearSession();
    setPlatformAdminFlag(false);
    qc.clear();
    navigate("/login", { replace: true });
  }
  const displayName = me?.fullName?.trim() || me?.username || "Tài khoản";
  const displayRole = roleLabel(me?.roleCodes);

  return (
    <header className="flex h-[50px] items-center gap-2 bg-card px-3 sm:gap-3 sm:px-4">
      {/* Chỉ hiện dưới md: ở desktop sidebar luôn mở, nút này không làm gì nên bị ẩn. */}
      <button
        type="button"
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:hidden"
        onClick={onMenuToggle}
        aria-label={navOpen ? "Đóng menu" : "Mở menu"}
        aria-expanded={navOpen}
      >
        {navOpen ? (
          <X className="h-5 w-5" aria-hidden="true" />
        ) : (
          <Menu className="h-5 w-5" aria-hidden="true" />
        )}
      </button>

      <div className="shrink-0">
        <BranchSwitcher />
      </div>

      <div className="min-w-0 flex-1">
        <GlobalSearch />
      </div>

      <div className="flex shrink-0 items-center gap-1 sm:gap-2">
        <ThemeToggle />
        <NotificationBell />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:px-2"
              aria-haspopup="menu"
            >
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted text-base"
                aria-hidden="true"
              >
                {me?.avatarUrl ? (
                  <img
                    src={me.avatarUrl}
                    alt=""
                    className="h-full w-full rounded-full object-cover"
                  />
                ) : (
                  "👨‍⚕️"
                )}
              </span>
              <span className="hidden min-w-0 flex-col items-start sm:flex">
                <span className="max-w-[10rem] truncate text-sm font-medium text-foreground">
                  {displayName}
                </span>
                <span className="max-w-[10rem] truncate text-xs text-muted-foreground">
                  {displayRole}
                </span>
              </span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="flex flex-col px-2 py-1.5">
              <strong className="truncate text-sm font-semibold text-foreground">
                {displayName}
              </strong>
              <span className="truncate text-xs text-muted-foreground">
                {me?.email || me?.username}
              </span>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/me/profile">Tài khoản của tôi</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/me/security">Bảo mật + MFA</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/me/preferences">Tuỳ chọn cá nhân</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings/clinic">Cài đặt phòng khám</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings/subscription">Gói dịch vụ</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={handleLogout}
              className="text-destructive focus:text-destructive"
            >
              Đăng xuất
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Clock />
      </div>
    </header>
  );
}
